import classNames from "classnames";
import React from "react";

import { useInfiniteScroll } from "@ascendnow/shared";

import styles from "./AscSelectInputOption.module.scss";

export interface IAscSelectInputOption<T = string | number> {
  value: T;
  label: string;
}
export interface AscSelectInputOptionProps<T = IAscSelectInputOption["value"]>
  extends IAscSelectInputOption<T> {
  disabled?: boolean;
  onClick?: (value: IAscSelectInputOption<T>) => void;
}

export function AscSelectInputOption<T = IAscSelectInputOption["value"]>({
  value,
  label,
  disabled = false,
}: AscSelectInputOptionProps<T>) {
  return <li>{label}</li>;
}

interface AscSelectOptionsContainerProps<T = IAscSelectInputOption["value"]> {
  options: AscSelectInputOptionProps<T>[];
  selectedOptions: IAscSelectInputOption<T>[];
  setSelectedOptions: React.Dispatch<
    React.SetStateAction<IAscSelectInputOption<T>[]>
  >;
  focusOnInput?: () => void;
  close?: () => void;
  multiple?: boolean;
  searchTerm?: string;
  className?: string;
  optionClassName?: string;
  selectedOptionClassName?: string;
  disabledOptionClassName?: string;
  onFetchNextOptions?: (searchValue?: string) => void;
}

export function AscSelectOptionsContainer<T = IAscSelectInputOption["value"]>({
  options,
  selectedOptions,
  setSelectedOptions,
  focusOnInput,
  close,
  searchTerm = "",
  multiple = false,
  className,
  optionClassName,
  onFetchNextOptions = () => [],
  selectedOptionClassName = styles.selected,
  disabledOptionClassName = styles.disabled,
}: AscSelectOptionsContainerProps<T>) {
  const { scrollRef, onScroll } = useInfiniteScroll<HTMLUListElement>(() =>
    onFetchNextOptions(searchTerm)
  );

  const isSelected = (value: T) =>
    selectedOptions.some((option) => option?.value === value);

  const selectOption = (option: IAscSelectInputOption<T>) => {
    if (!multiple) {
      setSelectedOptions([option]);
      return;
    }
    setSelectedOptions((options) => [
      ...(options?.filter((o) => o?.value !== option?.value) || []),
      option,
    ]);
  };

  const deselectOption = (value: T) =>
    setSelectedOptions((options) =>
      options?.filter((option) => option?.value !== value)
    );

  const toggleOption = (option: IAscSelectInputOption<T>) => {
    isSelected(option?.value)
      ? deselectOption(option?.value)
      : selectOption(option);
    multiple ? focusOnInput?.() : close?.();
  };

  return (
    <ul
      ref={scrollRef}
      onScroll={onScroll}
      className={classNames(styles.selectOptionsContainer, className)}
    >
      {options.map((option) => {
        if (
          !`${option?.value || ""}`
            ?.toLowerCase()
            ?.includes(searchTerm?.toLowerCase()) &&
          !option?.label?.toLowerCase()?.includes(searchTerm?.toLowerCase())
        ) {
          return null;
        }
        return (
          <li
            key={`${option?.value}`}
            onClick={() => {
              if (!option?.disabled) {
                toggleOption({
                  value: option?.value,
                  label: option?.label,
                });
                option?.onClick?.(option);
              }
            }}
            className={classNames(
              styles.selectOption,
              {
                [styles.disabled]: option?.disabled,
                [styles.selected]: isSelected(option?.value),
                [disabledOptionClassName]: option?.disabled,
                [selectedOptionClassName]: isSelected(option?.value),
              },
              optionClassName
            )}
          >
            {option?.label}
          </li>
        );
      })}
    </ul>
  );
}
